import { WidgetId } from 'types/idTypes';
import { Widget } from 'types/widget.types';

/**
 * This store handles updating multiple widgets at the same time when one widget updates.
 * Collects the widget data that should be updated from multiple widgets so that it can be dispatched at the same time to the store.
 *
 * E.g. If the text widget height increases - widgets below is shifted down relative to the height increase.
 * storedWidgetData -> [parentId] [memberWidgetIds] = { topPx: 50 }
 *
 */
class ResponsiveGroupBatchUpdate {
  private storedWidgetData: { [parentId: WidgetId]: { [widgetId: WidgetId]: Partial<Widget> } } = {};
  private static instance: ResponsiveGroupBatchUpdate;

  /**
   * Adds widget data updates for a specific widget for the next batch update
   *
   * @param groupWidgetId
   * @param widgetId
   * @param widgetData
   *
   */
  addWidgetData(groupWidgetId: WidgetId, widgetId: WidgetId, widgetData: Partial<Widget>) {
    if (!this.storedWidgetData[groupWidgetId]) this.storedWidgetData[groupWidgetId] = {};

    const currentWidgetData = this.storedWidgetData[groupWidgetId][widgetId];
    this.storedWidgetData[groupWidgetId][widgetId] = { ...currentWidgetData, ...widgetData };
  }

  /**
   * clears all the stored widget data for a parent widget
   * @param parentId
   */
  clear(parentId: WidgetId | undefined) {
    if (!parentId) return;
    if (!this.storedWidgetData[parentId]) return;

    delete this.storedWidgetData[parentId];
  }

  /**
   * Returns all the stored widget data for a parent widget
   * @param parentId
   *
   * Returns list of all the widgets that should be updated
   * @returns { widgetId: WidgetId; widgetData: Partial<Widget> }[]
   */
  getBatchUpdate(parentId: WidgetId | undefined) {
    if (!parentId) return [];

    const updates: { widgetId: WidgetId; widgetData: Partial<Widget> }[] = [];
    if (!this.storedWidgetData[parentId]) return updates;

    const widgetIds = Object.keys(this.storedWidgetData[parentId]);
    widgetIds.forEach((widgetId) => {
      updates.push({ widgetId, widgetData: this.storedWidgetData[parentId][widgetId] });
    });

    return updates;
  }

  /**
   * Returns the singleton instance of the store
   * @returns ResponsiveGroupBatchUpdate
   */
  public static getInstance(): ResponsiveGroupBatchUpdate {
    if (!ResponsiveGroupBatchUpdate.instance) {
      ResponsiveGroupBatchUpdate.instance = new ResponsiveGroupBatchUpdate();
    }

    return ResponsiveGroupBatchUpdate.instance;
  }
}

export const responsiveGroupBatchUpdate = ResponsiveGroupBatchUpdate.getInstance();
export type ResponsiveGroupBatchUpdateType = ResponsiveGroupBatchUpdate;
